/**
 * Service de validation des tokens JWT
 * Fournit des méthodes pour vérifier l'intégrité et la validité des tokens JWT
 */

import { NotificationService } from '../services/notification-service';

// Interface pour les claims JWT standard
export interface JwtClaims {
  // Standard claims
  iss?: string;       // Issuer
  sub?: string;       // Subject (généralement l'ID utilisateur)
  aud?: string | string[]; // Audience
  exp?: number;       // Expiration time
  nbf?: number;       // Not before
  iat?: number;       // Issued at
  jti?: string;       // JWT ID
  
  // Custom claims
  user_id?: number;   // ID utilisateur (spécifique à notre application)
  username?: string;  // Nom d'utilisateur (spécifique à notre application)
  email?: string;     // Email (spécifique à notre application)
  role?: string;      // Rôle (spécifique à notre application)
  is_staff?: boolean; // Est membre du staff (spécifique à notre application)
  is_superuser?: boolean; // Est superutilisateur (spécifique à notre application)
  
  // Autres claims possibles
  [key: string]: any;
}

export class JwtValidator {
  /**
   * Décode un token JWT sans vérifier la signature
   * @param token Token JWT à décoder
   * @returns Les claims du token décodé ou null si le token est invalide
   */
  static decodeToken(token: string): JwtClaims | null {
    try {
      if (!token) {
        console.warn('🔐 Token JWT vide ou non défini');
        return null;
      }
      
      // Vérifier le format du token (header.payload.signature)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('🔐 Format de token JWT invalide');
        return null;
      }
      
      // Décoder le payload (deuxième partie du token)
      const payload = parts[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      
      // Décoder en UTF-8
      try {
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        
        return JSON.parse(jsonPayload);
      } catch (error) {
        console.error('🔐 Erreur lors du décodage du payload JWT:', error);
        return null;
      }
    } catch (error) {
      console.error('🔐 Erreur lors du décodage du token JWT:', error);
      return null;
    }
  }
  
  /**
   * Vérifie si un token JWT est valide (non expiré, etc.)
   * @param token Token JWT à vérifier
   * @param options Options de validation
   * @returns true si le token est valide, false sinon
   */
  static validateToken(token: string, options: {
    checkExpiration?: boolean;
    checkNotBefore?: boolean;
    allowedIssuers?: string[];
    allowedAudiences?: string[];
    requiredClaims?: string[];
    showNotifications?: boolean;
  } = {}): boolean {
    // Options par défaut
    const {
      checkExpiration = true,
      checkNotBefore = true,
      allowedIssuers = [],
      allowedAudiences = [],
      requiredClaims = [],
      showNotifications = false
    } = options;
    
    try {
      // Décoder le token
      const claims = this.decodeToken(token);
      if (!claims) {
        if (showNotifications) {
          NotificationService.showTokenError('Token JWT invalide ou mal formé');
        }
        return false;
      }
      
      const currentTime = Math.floor(Date.now() / 1000); // Temps actuel en secondes
      const validationResults: { valid: boolean; reason?: string }[] = [];
      
      // Vérifier l'expiration
      if (checkExpiration && claims.exp) {
        const isNotExpired = currentTime < claims.exp;
        validationResults.push({
          valid: isNotExpired,
          reason: isNotExpired ? undefined : 'Token expiré'
        });
      }
      
      // Vérifier not before
      if (checkNotBefore && claims.nbf) {
        const isAfterNotBefore = currentTime >= claims.nbf;
        validationResults.push({
          valid: isAfterNotBefore,
          reason: isAfterNotBefore ? undefined : 'Token pas encore valide'
        });
      }
      
      // Vérifier l'émetteur
      if (allowedIssuers.length > 0 && claims.iss) {
        const validIssuer = allowedIssuers.includes(claims.iss);
        validationResults.push({
          valid: validIssuer,
          reason: validIssuer ? undefined : 'Émetteur non autorisé'
        });
      }
      
      // Vérifier l'audience
      if (allowedAudiences.length > 0 && claims.aud) {
        let validAudience = false;
        
        if (typeof claims.aud === 'string') {
          validAudience = allowedAudiences.includes(claims.aud);
        } else if (Array.isArray(claims.aud)) {
          validAudience = claims.aud.some(aud => allowedAudiences.includes(aud));
        }
        
        validationResults.push({
          valid: validAudience,
          reason: validAudience ? undefined : 'Audience non autorisée'
        });
      }
      
      // Vérifier les claims requis
      if (requiredClaims.length > 0) {
        const missingClaims = requiredClaims.filter(claim => !claims[claim]);
        const hasAllRequiredClaims = missingClaims.length === 0;
        
        validationResults.push({
          valid: hasAllRequiredClaims,
          reason: hasAllRequiredClaims ? undefined : `Claims manquants: ${missingClaims.join(', ')}`
        });
      }
      
      // Vérifier si toutes les validations ont réussi
      const isValid = validationResults.every(result => result.valid);
      
      // Si le token n'est pas valide et que les notifications sont activées
      if (!isValid && showNotifications) {
        const invalidReasons = validationResults
          .filter(result => !result.valid)
          .map(result => result.reason)
          .filter(Boolean);
        
        NotificationService.showTokenError(`Token JWT invalide: ${invalidReasons.join(', ')}`);
      }
      
      return isValid;
    } catch (error) {
      console.error('🔐 Erreur lors de la validation du token JWT:', error);
      
      if (showNotifications) {
        NotificationService.showTokenError(error);
      }
      
      return false;
    }
  }
  
  /**
   * Extrait les informations utilisateur d'un token JWT
   * @param token Token JWT à analyser
   * @returns Informations utilisateur extraites du token
   */
  static extractUserInfo(token: string): {
    userId?: number | string;
    username?: string;
    email?: string;
    role?: string;
    isStaff?: boolean;
    isSuperuser?: boolean;
  } | null {
    try {
      const claims = this.decodeToken(token);
      if (!claims) return null;
      
      // Extraire l'ID utilisateur (peut être stocké sous différentes clés)
      const userId = claims.user_id || claims.sub;
      
      // Extraire le nom d'utilisateur (peut être stocké sous différentes clés)
      const username = 
        claims.username || 
        claims.preferred_username || 
        claims.name ||
        (claims.email ? claims.email.split('@')[0] : undefined);
      
      // Extraire l'email
      const email = claims.email;
      
      // Extraire le rôle
      const role = claims.role || (claims.is_superuser ? 'admin' : (claims.is_staff ? 'staff' : 'user'));
      
      // Extraire les flags de staff et superuser
      const isStaff = !!claims.is_staff;
      const isSuperuser = !!claims.is_superuser;
      
      return {
        userId,
        username,
        email,
        role,
        isStaff,
        isSuperuser
      };
    } catch (error) {
      console.error('🔐 Erreur lors de l\'extraction des informations utilisateur du token JWT:', error);
      return null;
    }
  }
  
  /**
   * Calcule le temps restant avant l'expiration d'un token JWT
   * @param token Token JWT à analyser
   * @returns Temps restant en millisecondes, ou null si le token n'a pas de date d'expiration
   */
  static getTimeUntilExpiration(token: string): number | null {
    try {
      const claims = this.decodeToken(token);
      if (!claims || !claims.exp) return null;
      
      const expirationTime = claims.exp * 1000; // Convertir en millisecondes
      const currentTime = Date.now();
      const timeRemaining = expirationTime - currentTime;
      
      return Math.max(0, timeRemaining); // Ne pas retourner de valeur négative
    } catch (error) {
      console.error('🔐 Erreur lors du calcul du temps restant avant expiration:', error);
      return null;
    }
  }
  
  /**
   * Vérifie si un token JWT est sur le point d'expirer
   * @param token Token JWT à analyser
   * @param thresholdMs Seuil en millisecondes (par défaut 10 minutes)
   * @returns true si le token expire bientôt, false sinon
   */
  static isTokenAboutToExpire(token: string, thresholdMs: number = 10 * 60 * 1000): boolean {
    const timeRemaining = this.getTimeUntilExpiration(token);
    if (timeRemaining === null) return false;
    
    return timeRemaining < thresholdMs;
  }
}
